/**
 * Sentinel value used for the "create the proposed new entity" option in the
 * targetgroup/activity dropdowns. A row whose targetgroupId/activityId equals
 * this maps to a newTargetgroupLabel/newActivityLabel instead of an existing ID.
 */
export const NEW_ENTITY_VALUE = "__new__";

// The dropdown value is an existing entity ID only when it's set and not the
// "new" sentinel.
const asExistingId = (value) => (value && value !== NEW_ENTITY_VALUE ? value : null);

// A new-entity label survives only when the row is set to the "new" sentinel
// and the proposed label isn't blank.
const asNewLabel = (value, label) =>
    value === NEW_ENTITY_VALUE ? (label || "").trim() || null : null;

/**
 * Maps the review screen's editable rows to the payload saveMechanisms expects.
 * Keeps only accepted, grounded rows (label + evidence) and normalises empty
 * targetgroup/activity/effect selections to null (org-level / effect-less codes).
 * `mechanismId` marks a context-adding row for an existing TocMechanisms entry;
 * `category` is the taxonomy key for new mechanisms (re-gated server-side). A row
 * set to the NEW_ENTITY_VALUE sentinel proposes a new targetgroup/activity via
 * newTargetgroupLabel/newActivityLabel (standalone tenants only, gated server-side).
 *
 * @param {Array} rows review rows: { label, category, mechanismId, evidence, targetgroupId, activityId, effectId, newTargetgroupLabel, newActivityLabel, accepted }
 * @returns {Array} [{ label, category, mechanismId, suggestedTargetgroupId, suggestedActivityId, suggestedEffectId, newTargetgroupLabel, newActivityLabel, evidence }]
 */
const buildAcceptedPayload = (rows) =>
    (rows || [])
        .filter((r) => r.accepted && (r.label || "").trim() && (r.evidence || "").trim())
        .map((r) => ({
            label: r.label.trim(),
            category: r.category || "",
            mechanismId: r.mechanismId || null,
            suggestedTargetgroupId: asExistingId(r.targetgroupId),
            suggestedActivityId: asExistingId(r.activityId),
            suggestedEffectId: r.effectId || null,
            newTargetgroupLabel: asNewLabel(r.targetgroupId, r.newTargetgroupLabel),
            newActivityLabel: asNewLabel(r.activityId, r.newActivityLabel),
            evidence: r.evidence,
        }));

export default buildAcceptedPayload;
